import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UserService } from 'app/pages/com.usblick.common/auth/user.service';
import { ItineraryService } from 'app/shared/itinerary.service';
import { NotificationService } from 'app/shared/notifications/notification.service';
import { ProductFactoryService } from 'services/itinerary/products/product-factory.service';
import {
  getBoatCoverImage,
  getBoatCurrency,
  getBoatLogo
} from 'app/content-result/category-result/result/base-result/information/categories/boat-result/boat-result-functions';

@Component({
  selector: 'usb-modal-boat',
  templateUrl: './modal-boat.component.html',
  styleUrls: ['./modal-boat.component.scss']
})
export class ModalBoatComponent implements OnInit {

  @Input() boat: any;
  @Input() payload: any;
  @Input() arrayResults: any;
  @Input() tabActive: string;
  @Input() clientSetting: any;
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() action = '';
  @Output() refModal = new EventEmitter<string>();

  readonly tabs = [
    { key: 'boat', label: 'EMBARCACIÓN' },
    { key: 'info', label: 'INFO' },
    { key: 'photos', label: 'FOTOS' },
    { key: 'comments', label: 'COMENTARIOS' },
  ];

  tab = 'boat';
  showEquipment = true;
  buttonColorActive: string;
  buttonTextActive: string;

  // Cada opción es una semana de charter disponible del mismo barco, con su propio precio.
  options: any[] = [];
  selectedOption: any;
  cheapestOption: any;

  mandatoryExtras: any[] = [];
  optionalExtras: any[] = [];
  // Extras opcionales marcados por el usuario, por extraid. Se usarán al agregar al itinerario.
  selectedExtras = new Set<string>();

  // Evita doble clic en Continuar mientras se agrega el producto.
  processing = false;

  constructor(
    private productFactory: ProductFactoryService,
    private itineraryService: ItineraryService,
    private notificationService: NotificationService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const sidebar = JSON.parse(this.clientSetting?.clients_setting_app_booking?.bookingFlow_sidebar || '{}');
    this.buttonColorActive = sidebar?.background_color_header;
    this.buttonTextActive = sidebar?.color;

    if (this.tabs.some(tab => tab.key === this.tabActive)) {
      this.tab = this.tabActive;
    }

    this.options = this.info?.options || [];
    // Solo una opción lleva el sello: la primera con el menor precio (si hay empate no se repite).
    this.cheapestOption = this.options.reduce((cheapest: any, option: any) =>
      !cheapest || Number(option.priceGross) < Number(cheapest.priceGross) ? option : cheapest, null);
    // Con una sola semana no hay nada que elegir; con varias, el usuario escoge y recién ahí
    // se muestran los extras de esa semana y el resumen de pago.
    if (this.options.length === 1) {
      this.selectOption(this.options[0]);
    }
  }

  selectOption(option: any): void {
    if (!option || option === this.selectedOption) {
      return;
    }
    this.selectedOption = option;
    this.selectedExtras.clear();

    const extras = option.extras || this.info?.extras || [];
    this.mandatoryExtras = extras.filter((extra: any) => this.isMandatory(extra));
    this.optionalExtras = extras.filter((extra: any) => !this.isMandatory(extra));
  }

  isCheapest(option: any): boolean {
    return option === this.cheapestOption;
  }

  get info(): any {
    return this.boat?.additionalInfo;
  }

  get details(): any {
    return this.info?.boat;
  }

  get title(): string {
    return this.details?.boatName || this.boat?.title;
  }

  get coverImage(): string {
    return getBoatCoverImage(this.boat);
  }

  get logo(): string {
    return getBoatLogo(this.boat);
  }

  get currency(): string {
    return getBoatCurrency(this.boat);
  }


  get isRefundable(): boolean {
    return Array.isArray(this.info?.cancellationPolicy) && this.info.cancellationPolicy.length > 0;
  }

  // Extras que se cobran: todos los obligatorios más los opcionales marcados.
  get chargedExtras(): any[] {
    return [...this.mandatoryExtras, ...this.optionalExtras.filter(extra => this.selectedExtras.has(extra.extraid))];
  }

  // Los extras vienen en la misma moneda que la opción, así que se suman directo.
  get onlineTotal(): number {
    const extrasOnline = this.chargedExtras.filter(extra => !this.paysOnSite(extra));
    return (Number(this.selectedOption?.priceGross) || 0) + this.sumExtras(extrasOnline);
  }

  get onSiteTotal(): number {
    return this.sumExtras(this.chargedExtras.filter(extra => this.paysOnSite(extra)));
  }

  async continue(): Promise<void> {
    if (!this.selectedOption || this.processing) {
      return;
    }
    this.processing = true;
    const loadingToast = this.notificationService.loading('Procesando producto...');

    try {
      // Guest sin file todavía: se crea antes de armar el producto (createProduct lee el itinerario activo).
      await this.itineraryService.ensureGuestItinerary();

      const categoryId: number = (Array.isArray(this.payload) ? this.payload[0] : this.payload).categoriesId[0];
      const productService = this.productFactory.getProductService(categoryId);
      const product = productService.createProduct({
        item: this.boat,
        product: this.boat,
        payload: this.payload,
        categoryId,
        othersPerCategory: {
          option: this.selectedOption,
          extras: this.chargedExtras,
          searchId: this.arrayResults?.searchId,
          userService: this.userService
        }
      });
      this.notificationService.close(loadingToast);

      if (!product) {
        this.notificationService.warning('No se pudo crear el producto');
        this.processing = false;
        return;
      }

      this.itineraryService.add(product).subscribe({
        next: (response: any) => {
          if (response) {
            this.itineraryService.evaluateDateItineraryRange(this.itineraryService.itinerary['items']);
            this.itineraryService.updateItinerary(this.itineraryService.itinerary);
            this.notificationService.success('¡Felicitaciones! Producto agregado con éxito.');
            this.close();
          } else {
            this.notificationService.error('No se recibió confirmación del servidor', 'Error');
          }
          this.processing = false;
        },
        error: (error: any) => {
          console.error('Error adding product:', error);
          this.notificationService.error('Ocurrió un error al agregar el producto', 'Error');
          this.processing = false;
        }
      });
    } catch (error) {
      this.notificationService.close(loadingToast);
      console.error('Error al procesar el barco:', error);
      this.notificationService.error('Ocurrió un error al procesar la solicitud');
      this.processing = false;
    }
  }

  extraAmount(extra: any): number {
    return Number(extra?.options?.[0]?.netcost?.amount) || 0;
  }

  paysOnSite(extra: any): boolean {
    return extra?.options?.[0]?.paymentOffice === true;
  }

  allPayOnSite(extras: any[]): boolean {
    return extras.length > 0 && extras.every(extra => this.paysOnSite(extra));
  }

  extraCurrency(extra: any): string {
    return extra?.options?.[0]?.netcost?.currency || this.currency;
  }

  toggleExtra(extra: any): void {
    if (this.selectedExtras.has(extra.extraid)) {
      this.selectedExtras.delete(extra.extraid);
    } else {
      this.selectedExtras.add(extra.extraid);
    }
  }

  close(): void {
    this.refModal.emit('close');
  }

  private sumExtras(extras: any[]): number {
    return extras.reduce((total, extra) => total + this.extraAmount(extra), 0);
  }

  private isMandatory(extra: any): boolean {
    return String(extra?.ismandatory) === 'true';
  }
}
